import React, {useCallback, useMemo} from 'react';

import {bridge} from 'charts/index';
import {Chart} from 'charts/components/Chart';
import SimpleLineSeries from 'charts/components/series/SimpleLineSeries';
import {NumberValue, ScaleLinear, scaleLinear, scaleTime} from 'd3-scale';
import {extent} from 'd3-array';

import {formatKpiValue, KpiTargetNumericType} from '@store/@types/kpi';

import SimpleVerticalAxis from 'charts/components/axes/SimpleVerticalAxis';
import SimpleBottomAxis from 'charts/components/axes/SimpleBottomAxis';
import {DataPoint} from 'charts/types';
import PointSeries from 'charts/components/series/PointSeries';
import {shortNumberFormat} from 'utils/shortNumberFormat';
import {UnwrappedTooltip} from 'components/pure/tooltips/UnwrappedTooltip';
import {line} from 'd3-shape';
import moment from 'moment';
import {DateFormat} from 'utils/datetime/DateFormat';

const margins = {top: 0, left: 0, right: 0, bottom: 0};
const wrapStyle = {
  paddingLeft: 40,
  paddingBottom: 24,
  paddingRight: 2,
  width: 380,
};

export interface CoachingGoalsChartDataItem {
  numericType: KpiTargetNumericType;
  color: string;
  // KPI Id
  metricId: number;
  // KPI Name
  metricName: string;
  dataPoints: Array<DataPoint>;
  // Kpi Goal
  goal: number;
}

interface Props {
  planStart: Date;
  from: Date;
  to: Date;
  data: Array<CoachingGoalsChartDataItem>;
  height?: number;
  width?: number;
}

export const CoachingGoalsChart: React.FC<Props> = ({
  from,
  to,
  data,
  height = 100,
  width = 300,
  planStart,
}) => {
  const xScale = useMemo(() => {
    return scaleTime()
      .domain([from, to])
      .range([margins.left, width - margins.right]);
  }, [from, to, width]);

  const yScaleDefault = useMemo(() => {
    return scaleLinear()
      .domain([0, 100])
      .range([height - margins.bottom, margins.top]);
  }, [height]);

  const [yScale1, firstAxisType] = useMemo(() => {
    // Find the first relevant axis type
    const type = data.find(d => d.dataPoints.length !== 0)?.numericType;

    return getScale(data, type, height);
  }, [data, height]);

  const [yScale2, secondAxisType] = useMemo(() => {
    // Find the first relevant axis type (not matching firstAxis)
    const type = data.find(
      d => d.dataPoints.length !== 0 && d.numericType !== firstAxisType
    )?.numericType;

    return getScale(data, type, height, 1.15);
  }, [data, firstAxisType, height]);

  const getYScale = useCallback(
    (type?: KpiTargetNumericType) => {
      if (type === firstAxisType) return yScale1;
      if (type === secondAxisType) return yScale2;
    },
    [firstAxisType, secondAxisType, yScale1, yScale2]
  );

  const diff = moment(to).diff(moment(from)) / 2;

  return (
    <div style={wrapStyle}>
      <Chart svgDimensions={{height, width}} margins={margins}>
        <g>
          {yScale1 && firstAxisType ? (
            <SimpleVerticalAxis
              amtOfTicks={5}
              margins={margins}
              scale={yScale1}
              tickFormat={val => axisTickFormat(val as number, firstAxisType)}
            />
          ) : (
            <SimpleVerticalAxis
              amtOfTicks={5}
              margins={margins}
              scale={yScaleDefault}
              tickFormat={val => axisTickFormat(val as number, secondAxisType)}
            />
          )}
          {yScale2 && secondAxisType && (
            <SimpleVerticalAxis
              amtOfTicks={5}
              margins={{...margins, left: width}}
              scale={yScale2}
              isRight
              tickFormat={val => axisTickFormat(val as number, secondAxisType)}
            />
          )}
          <SimpleBottomAxis
            scale={xScale}
            secondPeriodStart={moment(from).add(diff).toDate()}
            marginBottom={margins.bottom}
            chartHeight={height}
            extraTicks={[planStart]}
          />
        </g>
        {data.map(({color, metricId, metricName, dataPoints, numericType, goal}) => {
          const yScale = getYScale(numericType);
          if (!yScale) {
            return <React.Fragment key={metricId} />;
          }

          const hiddenLine = line<DataPoint>()
            .x(d => xScale(d.date)!)
            .y(d => yScale(d.value)!)(bridge(from, to, [{value: goal, date: from}]));

          return (
            <React.Fragment key={metricId}>
              <g>
                <rect height={100} width={xScale(planStart)} fill="#CFC3C933" />
              </g>
              <SimpleLineSeries
                data={bridge(from, to, [{value: goal, date: from}])}
                color={color}
                xScale={xScale}
                yScale={yScale}
                dashed
              />
              {
                // The dashed line isn't easy to mouseover so we have a hidden one with it that
                // controls the tooltip events
                hiddenLine && (
                  <UnwrappedTooltip
                    content={`KPI Target: ${metricName} (${formatKpiValue(numericType, goal)})`}>
                    <path
                      d={hiddenLine}
                      stroke={'#FFFFFF00'}
                      fill="none"
                      strokeWidth={6}
                      cursor={'auto'}
                    />
                  </UnwrappedTooltip>
                )
              }

              <SimpleLineSeries
                data={bridge(from, to, dataPoints)}
                color={color}
                xScale={xScale}
                yScale={yScale}
              />

              <PointSeries
                isEmptyCore
                data={dataPoints}
                color={color}
                xScale={xScale}
                yScale={yScale}
                radius={3}
                popContent={({date, value, extra}) => {
                  const userName = extra?.observedBy?.getFullName();
                  return (
                    <div>
                      <h4>{metricName}</h4>
                      <p>
                        {formatKpiValue(numericType, value, {includeUnits: true})}
                        {' - '}
                        entered on {moment(date).format(DateFormat.MM_DD_YYYY)}
                        {userName && ` by ${userName}`}
                      </p>
                    </div>
                  );
                }}
              />
            </React.Fragment>
          );
        })}
      </Chart>
    </div>
  );
};

function getScale(
  data: CoachingGoalsChartDataItem[],
  type: KpiTargetNumericType | undefined,
  height: number,
  paddingCoefficient = 1.1 // 5% padding more or less
): [ScaleLinear<number, number>, KpiTargetNumericType] | [] {
  if (!type) return [];

  // Pull out all the values
  const allValues = data
    .filter(d => d.numericType === type)
    .flatMap(d => [...d.dataPoints.map(el => el.value), d.goal]);

  // This is added to keep points from using the top of the graph.
  const padding = Math.max(...allValues) * paddingCoefficient;

  // Return a range including 0, all given values, and the kpi's goal
  const scale = scaleLinear()
    .domain(extent([0, ...allValues, padding]) as [NumberValue, NumberValue])
    .range([height - margins.bottom, margins.top]);

  return [scale, type];
}

function axisTickFormat(val: number, type?: KpiTargetNumericType): string {
  if (!type) return '';

  // Dont use suffixes for time values
  if (type === KpiTargetNumericType.TIME) {
    return formatKpiValue(type, val, {forceHours: true});
  }

  const [valueForFormat, suffix] = shortNumberFormat(val);

  // Place the suffix before the %
  if (type === KpiTargetNumericType.PERCENTAGE) {
    return formatKpiValue(type, valueForFormat as number).replace('%', `${suffix}%`);
  }

  return formatKpiValue(type, valueForFormat as number) + suffix;
}
